// Dependency imports
import { useEffect, useRef, useState } from 'react';
import Peer, { SignalData } from 'simple-peer';
import { useDispatch } from 'react-redux';

// Local imports
import { useInferenceSocket } from './useInferenceSocket';
import useVideo from './video';
import { setPreventNavigation } from 'store/preventNavigation/preventNavigationSlice';
import { useSnack } from 'plugins/snack';

export const useLiveInference = () => {
  const [stream, setStream] = useState<MediaStream>();
  const [modelData, setModelData] = useState<any>();
  const [modelLoaded, setModelLoaded] = useState<boolean>(false);
  const [started, setStarted] = useState(false);
  const [isReseting, setResetting] = useState(false);
  const [activityData, setActivityData] = useState<any>([]);
  const connectionRef = useRef<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [image, setImage] = useState<any>();
  const [streamError, setStreamError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const snack = useSnack();
  const { videoRef } = useVideo();
  const { socket, sendMessage, connectionStatus } = useInferenceSocket();

  useEffect(() => {
    if (started) {
      setTimeout(() => {
        if (!modelLoaded) {
          setModelLoaded(true);
        }
      }, 15000);
    }
  }, [started]);

  const handleMessageReceived = (message: any) => {
    const messageType = message?.type;
    const data = message?.data;

    switch (messageType) {
      case 'answer':
        connectionRef.current?.signal(data);
        break;
      case 'live_inf_output':
        setActivityData((prevData: any) => {
          console.log('data activity', data.activity);
          let activityName = data.activity.activity;
          let updatedData = [...prevData];

          if (
            updatedData.length > 0 &&
            updatedData[updatedData.length - 1].activity === activityName
          ) {
            updatedData[updatedData.length - 1] = data.activity;
            return updatedData;
          } else {
            activityName = data.activity.activity;
            return [...prevData, data.activity];
          }
        });
        break;
      case 'success':
        snack({
          message: message.message,
          severity: 'success'
        });
        setResetting(false);
        break;
      case 'error':
        setStreamError(message.message);
        break;
      case 'loaded':
        setLoading(false);
        break;
      case 'model_loaded':
        setModelLoaded(message.data.model_loaded);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    socket?.addEventListener('message', (event: any) => {
      if (event.data instanceof Blob) {
        // Handle Blob (assuming it's an image)
        const reader = new FileReader();
        reader.onload = () => {
          const imageUrl = reader.result as string;
          setImage(imageUrl);
        };
        reader.readAsDataURL(event.data);
      } else {
        // Handle JSON data
        try {
          const message = JSON.parse(event.data);
          handleMessageReceived(message);
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      }
    });

    return () => {
      if (socket) {
        socket.close();
        socket.removeEventListener('message', () => {});
      }
    };
  }, [socket]);

  const startStream = (url: string, cameraId: string) => {
    if (socket) {
      sendMessage(
        JSON.stringify({
          event: 'start_live_stream',
          data: {
            camera_id: cameraId,
            url: url
          }
        })
      );
    }
  };

  const resumeInference = () => {
    setStarted(true);
    setActivityData([]);
    dispatch(setPreventNavigation(true));
    if (socket) {
      sendMessage(
        JSON.stringify({
          event: 'resume'
        })
      );
    }
  };

  const restartInference = (shouldStart: boolean) => {
    if (shouldStart) {
      setModelLoaded(false);
      setStarted(true);
      setActivityData([]);
      dispatch(setPreventNavigation(true));
    }

    if (socket) {
      sendMessage(
        JSON.stringify({
          event: 'restart'
        })
      );
    }
  };

  const endStream = () => {
    if (socket) {
      sendMessage(
        JSON.stringify({
          event: 'end_live_stream'
        })
      );
    }
  };

  useEffect(() => {
    if (!started) {
      setImage('');
    }
  }, [started]);

  const startLive = (url: string, cameraId: string) => {
    // const peerCon = createRTCPeerConnection();
    setStarted(true);
    setImage('');
    setActivityData([]);
    dispatch(setPreventNavigation(true));
    const peer = new Peer({ initiator: true, trickle: false, stream });
    connectionRef.current = peer;
    peer.on('signal', (signal: SignalData) => {
      if (socket) {
        sendMessage(
          JSON.stringify({
            event: 'start_live_inference',
            data: {
              signal: signal,
              modelData: modelData,
              url: url,
              camera_id: cameraId
            }
          })
        );
      }
    });
    peer.on('stream', currentStream => {
      videoRef.current!.srcObject = currentStream;
    });

    peer.on('track', (track, stream) => {
      console.log('track', track, stream);
    });

    peer.on('connect', () => {
      console.log('connected');
    });
  };

  const endInference = () => {
    setTimeout(() => setActivityData([]), 4000);
    setStarted(false);
    dispatch(setPreventNavigation(false));
    sendMessage(
      JSON.stringify({
        event: 'end_live_inference'
      })
    );

    if (connectionRef.current) {
      connectionRef.current.destroy();
      connectionRef.current = null;
    }
  };

  return {
    socket,
    videoRef,
    startLive,
    modelData,
    loading,
    setLoading,
    connectionStatus,
    modelLoaded,
    started,
    endInference,
    startStream,
    resumeInference,
    restartInference,
    streamError,
    endStream,
    activityData,
    setModelData,
    setStreamError,
    image,
    setImage,
    sendMessage,
    isReseting,
    setResetting,
    setModelLoaded
  };
};
